import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="33116" className="flex gap-8">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-5 w-20 bg-white/5 animate-pulse rounded-sm" />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="relative text-sm font-medium uppercase tracking-[0.1em] text-primary transition-colors hover:text-primary/90 after:absolute after:bottom-[-4px] after:left-1/2 after:h-[1px] after:w-0 after:bg-primary after:transition-all after:duration-300 after:ease-out hover:after:left-0 hover:after:w-full font-sans">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 p-8">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-8 w-32 bg-white/5 animate-pulse rounded-sm" />)}
      </div>;
  }
  return <nav className="flex flex-col items-center justify-center gap-8 p-8">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="text-2xl font-bold uppercase tracking-widest text-primary transition-colors hover:text-primary/90 font-sans" onClick={onClose}>
          {item.label}
        </Link>)}
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobileMenuOpen]);
  return <header id="wts-header" className="sticky top-0 z-50 w-full transition-all duration-500 dark text-foreground border-b border-border !h-[90px] !bg-[hsl(0,0%,100%)]">
      <div className="container mx-auto flex h-20 items-center justify-between ![padding:9px_48px_0px_48px]">
        {}
        <Link to="#" className="flex items-center gap-2 z-50">
          <img src="https://suitable-martin.10web.cloud/wp-content/uploads/2026/03/WTS-LOGO-FULL-COLOR-NO-BORDER.png" alt="Workplace Technology Solutions" className="object-contain !w-[155px] !h-[80px]" />
        </Link>

        {}
        <div className="hidden md:block">
          <MenuProvider menu_id="16">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        {}
        <div className="hidden md:block">
          <Button asChild className="h-10 rounded-[4px] bg-primary px-6 text-xs font-bold uppercase tracking-wider text-primary-foreground shadow-[0_0_15px_rgba(225,29,72,0.5)] transition-all duration-300 hover:bg-primary/90 hover:shadow-[0_0_25px_rgba(225,29,72,0.7)]">
            
          </Button>
        </div>

        {}
        <Button variant="ghost" size="icon" className="z-50 md:hidden text-foreground hover:bg-transparent hover:text-primary" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
          {isMobileMenuOpen ? <X className="h-6 w-6 text-primary" /> : <Menu className="h-6 w-6 text-primary" />}
        </Button>

        {}
        <div className={`fixed inset-0 z-40 flex h-screen w-screen flex-col items-center justify-center bg-background/95 backdrop-blur-2xl transition-all duration-500 md:hidden ${isMobileMenuOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}>
          <MenuProvider menu_id="16">
            <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
          </MenuProvider>
        </div>
      </div>
    </header>;
}